import { useState } from 'react';
import { Phone, Globe, Clock, MapPin, Navigation, Share2, Star, Bookmark, BookmarkCheck, Copy, Check, ExternalLink, Building2, Tag, Info, Mail, MessageCircle, Utensils, Wifi, Snowflake, Car, Truck, Sun, Accessibility, ShoppingBag, X, PenLine, Facebook, Instagram, Save, Loader2, Footprints, Bike, Ruler, CheckCircle2, XCircle, Eye, Bus, CloudRain } from 'lucide-react';
import { TRANSIT_STOP_FACILITIES, resolveTransitStopFacilities } from '../../utils/transitStopFacilities';
import { useMapStore } from '../../stores/mapStore';
import { useAuthStore } from '../../stores/authStore';
import { getCategoryLabel, getCategoryIconName } from '../../constants/categories';
import { DynamicIcon } from '../ui/DynamicIcon';
import { buildNativePlaceAddress, addressForClipboard, formatPostcodeFull, formatPostcodesInText } from '../../utils/postcode';
import { updatePlaceDetails, ratePlace, type ContributionDetail } from '../../services/api';
import { formatDistance as fmtDist } from '../../services/mapbox';
import { useSettingsStore } from '../../stores/settingsStore';
import { MARKETING_URL } from '../../lib/env';
import { isAdminPlaceType, placeShowsBusinessDetails, resolveAdminLevel } from '../../utils/contributionPlaceAdmin';
import { isTransitStopPlaceType } from '../../utils/transitStop';
import {
  coordalAdminCodeLabel,
  ghanaPostGpsAdminCodeLabel,
  gssOfficialCodeLabel,
  showGssOfficialCode,
} from '../../utils/adminBoundaryFocus';
import { OpeningHoursEditor } from '../shared/OpeningHoursEditor';
import { openingHoursPrefill } from '../../utils/openingHours';
import {
  GHANA_POST_GPS_MAX_LENGTH,
  isValidFullGhanaPostGps,
  sanitizeGhanaPostGpsInput,
} from '../../utils/ghanaPostGps';
import { formatHoursForSubtitle } from '../search/SearchResultItem';
import { floodRiskBadgeClass, floodRiskLabel, FLOOD_EXPOSURE_DISCLAIMER } from '../../lib/floodExposure';
import { BuildingUnitsDetailSection } from './BuildingUnitsDetailSection';
import { TransitStopOperatorList, TransitStopRouteList } from './TransitStopRouteList';
import { normalizeBuildingUnits } from '../../utils/buildingUnitsReview';

const CONTRIBUTION_PREVIEW_STATUS = {
  pending: {
    label: 'Under review',
    icon: Clock,
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
    panel: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    badge: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
    panel: 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
    panel: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800',
  },
} as const;

function placeHoursBadgeClass(status: string): string {
  if (status.startsWith('Open')) {
    return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
  }
  if (status.startsWith('Closed')) {
    return 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }
  return 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300';
}

function haversineMtr(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000, r = Math.PI / 180;
  const dl = (lat2 - lat1) * r, dd = (lon2 - lon1) * r;
  const a = Math.sin(dl / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dd / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function travelTime(meters: number, speedMs: number): string {
  const secs = meters / speedMs;
  if (secs < 60) return `${Math.round(secs)}s`;
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

interface PlaceDetailsProps {
  onGetDirections?: () => void;
  onSavePlace?: () => void;
  onClose?: () => void;
  onSuggestEdit?: () => void;
  contributionPreview?: ContributionDetail | null;
  onDismissContributionPreview?: () => void;
}

type EditableField = 'phone' | 'whatsapp' | 'email' | 'website' | 'facebook' | 'instagram' | 'opening_hours' | 'ghana_post_gps';

export function PlaceDetails({
  onGetDirections,
  onSavePlace,
  onClose,
  onSuggestEdit,
  contributionPreview,
  onDismissContributionPreview,
}: PlaceDetailsProps) {
  const { selectedPlace, clearSelectedPlace, selectPlace, userLocation } = useMapStore();
  const { savedPlaces, requireAuth, removeSavedPlace } = useAuthStore();
  const { settings: { distanceUnit } } = useSettingsStore();
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedPostcode, setCopiedPostcode] = useState(false);
  const [copiedGPS, setCopiedGPS] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  // Inline edit state
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Rating state
  const [hoverRating, setHoverRating] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [isRating, setIsRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  const [openingHoursEditValue, setOpeningHoursEditValue] = useState('');

  if (!selectedPlace) return null;

  const isAdminPlace = isAdminPlaceType(selectedPlace.placeType);
  const isTransitStop = isTransitStopPlaceType(selectedPlace.placeType);
  /** POI/business sections — hidden for admin, streets, transit, and natural area features. */
  const showBusinessDetails = placeShowsBusinessDetails({
    placeType: selectedPlace.placeType,
    category: selectedPlace.category,
  });
  const hasTransitStopDetails = isTransitStop;
  const transitFacilityValues = isTransitStop
    ? resolveTransitStopFacilities(selectedPlace, contributionPreview?.extra_fields)
    : null;
  const transitDescription = (
    selectedPlace.description?.trim()
    || contributionPreview?.description?.trim()
    || ''
  );
  const transitDescriptionSourceUrl = (
    selectedPlace.descriptionSourceUrl?.trim()
    || (typeof contributionPreview?.extra_fields?.description_source_url === 'string'
      ? contributionPreview.extra_fields.description_source_url.trim()
      : '')
  );
  const transitRoutes = selectedPlace.routes?.length
    ? selectedPlace.routes
    : (Array.isArray(contributionPreview?.extra_fields?.routes)
      ? contributionPreview!.extra_fields!.routes.map(String).filter(Boolean)
      : []);
  const transitOperators = selectedPlace.operators?.length
    ? selectedPlace.operators
    : (Array.isArray(contributionPreview?.extra_fields?.operators)
      ? contributionPreview!.extra_fields!.operators.map(String).filter(Boolean)
      : []);
  const adminLevel = resolveAdminLevel(selectedPlace.category, selectedPlace.adminLevel);
  const isStreetRoad = selectedPlace.placeType === 'street' || selectedPlace.category === 'road';
  const isBuildingPlace = selectedPlace.placeType === 'building';
  const floodRiskLevel = selectedPlace.floodRiskLevel;
  const showFloodExposure = (isStreetRoad || isBuildingPlace) && Boolean(floodRiskLevel);

  const coordalCode = selectedPlace.ghanaPostCode?.trim().toUpperCase() || '';
  const ghanaPostGpsCode = selectedPlace.ghanaPostGPS?.trim().toUpperCase() || '';
  const gssCode = selectedPlace.gssPcode?.trim().toUpperCase() || '';
  const showGssCode = showGssOfficialCode(
    selectedPlace.category,
    adminLevel,
    selectedPlace.adminSource,
    gssCode,
    coordalCode,
  );

  const formattedPostcode = isAdminPlace ? '' : formatPostcodeFull(selectedPlace.ghanaPostCode);

  const addressParts = {
    name: selectedPlace.name,
    streetAddress: selectedPlace.streetAddress,
    addrStreet: selectedPlace.addrStreet,
    addrSuburb: selectedPlace.addrSuburb,
    addrCity: selectedPlace.addrCity,
    address: selectedPlace.address,
    district: selectedPlace.district,
    region: selectedPlace.region,
    postcode: isAdminPlace ? undefined : selectedPlace.ghanaPostCode,
    ghanaPostGps: selectedPlace.ghanaPostGPS,
    houseNumber: selectedPlace.houseNumber,
    frontageStreetAddress: selectedPlace.frontageStreetAddress,
  };

  const nativeAddress = selectedPlace.address
    ? formatPostcodesInText(selectedPlace.address)
    : buildNativePlaceAddress(addressParts);

  const copyAddress = addressForClipboard(selectedPlace.name, nativeAddress);

  // Distance & travel times from user location
  const distanceMeters = userLocation
    ? haversineMtr(userLocation.lat, userLocation.lng, selectedPlace.coordinates.lat, selectedPlace.coordinates.lng)
    : null;
  const displayDistance = distanceMeters != null ? fmtDist(distanceMeters, distanceUnit) : null;
  const walkTime = distanceMeters != null ? travelTime(distanceMeters, 1.4) : null;
  const bikeTime = distanceMeters != null ? travelTime(distanceMeters, 4.2) : null;
  const driveTime = distanceMeters != null ? travelTime(distanceMeters, 8.3) : null;

  // Check if place is already saved
  const savedPlace = savedPlaces.find(p => p.placeId === selectedPlace.id);
  const isSaved = !!savedPlace;

  const handleGetDirections = () => onGetDirections?.();

  const handleSavePlace = () => {
    if (isSaved && savedPlace) {
      removeSavedPlace(savedPlace.id);
    } else {
      requireAuth(() => onSavePlace?.());
    }
  };

  const handleClose = () => {
    if (contributionPreview && onDismissContributionPreview) {
      onDismissContributionPreview();
      return;
    }
    if (onClose) onClose();
    else clearSelectedPlace();
  };

  // Share link with coordinates
  const handleShare = async () => {
    const baseUrl = window.location.origin + window.location.pathname;
    const shareUrl = `${baseUrl}?lat=${selectedPlace.coordinates.lat.toFixed(6)}&lng=${selectedPlace.coordinates.lng.toFixed(6)}&zoom=20&place=${encodeURIComponent(selectedPlace.id)}`;
    const shareData = {
      title: selectedPlace.name,
      text: `${selectedPlace.name} — ${selectedPlace.address}`,
      url: shareUrl,
    };

    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 2000);
      } catch { /* ignore */ }
    }
  };

  const handleCall = (number: string) => { window.location.href = `tel:${number}`; };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(copyAddress);
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } catch {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    }
  };

  const handleCopyPostcode = async () => {
    const code = isAdminPlace ? coordalCode : formattedPostcode;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopiedPostcode(true);
      setTimeout(() => setCopiedPostcode(false), 2000);
    } catch {
      setCopiedPostcode(true);
      setTimeout(() => setCopiedPostcode(false), 2000);
    }
  };

  const handleCopyGPS = async () => {
    const gps = ghanaPostGpsCode || selectedPlace.ghanaPostGPS;
    if (!gps) return;
    try {
      await navigator.clipboard.writeText(gps);
      setCopiedGPS(true);
      setTimeout(() => setCopiedGPS(false), 2000);
    } catch {
      setCopiedGPS(true);
      setTimeout(() => setCopiedGPS(false), 2000);
    }
  };

  // --- Ghana PostGPS validation ---
  const validateGhanaPostGPS = (value: string): string | null => {
    const v = value.trim().toUpperCase();
    if (!isValidFullGhanaPostGps(v)) {
      return 'Format: XX-000-0000 or GH-XX000-0000 (e.g. AK-039-5028 or GH-GA141-2554)';
    }
    return null;
  };

  // Only allow editing for DB-backed places (loc_* IDs from backend)
  const isDbPlace = selectedPlace.id?.startsWith('loc_');

  // --- Inline edit handlers ---
  const startEdit = (field: EditableField) => {
    if (!isDbPlace) return;
    setEditingField(field);
    setEditError('');
    if (field === 'opening_hours') {
      setOpeningHoursEditValue(openingHoursPrefill(selectedPlace.openingHours, selectedPlace.hours));
      setEditValue('');
    } else {
      // Pre-fill with existing value if present
      const currentValues: Record<EditableField, string | undefined> = {
        phone: selectedPlace.phone,
        whatsapp: selectedPlace.whatsapp,
        email: selectedPlace.email,
        website: selectedPlace.website,
        facebook: selectedPlace.facebook,
        instagram: selectedPlace.instagram,
        opening_hours: undefined,
        ghana_post_gps: selectedPlace.ghanaPostGPS,
      };
      setEditValue(currentValues[field] || '');
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setEditError('');
  };

  const saveEdit = async () => {
    if (!editingField || !selectedPlace.id) return;

    let valueToSave: string;
    if (editingField === 'opening_hours') {
      valueToSave = openingHoursEditValue.trim();
      if (!valueToSave) { setEditError('Please set at least one time range or choose Open 24/7'); return; }
    } else {
      valueToSave = editValue.trim();
      if (!valueToSave) return;
    }

    // Frontend validation
    if (editingField === 'ghana_post_gps') {
      const err = validateGhanaPostGPS(valueToSave);
      if (err) { setEditError(err); return; }
      valueToSave = valueToSave.toUpperCase();
    }

    setIsSaving(true);
    setEditError('');
    try {
      await updatePlaceDetails(selectedPlace.id, { [editingField]: valueToSave });
      selectPlace(selectedPlace.id);
      setEditingField(null);
      setEditValue('');
    } catch (err: any) {
      console.error('Failed to update:', err);
      const msg = err?.message || err?.detail || 'Failed to save. Please try again.';
      setEditError(typeof msg === 'string' ? msg : 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Build amenity tags
  const amenityTags: { label: string; icon: React.ReactNode }[] = [];
  if (selectedPlace.amenities) {
    const a = selectedPlace.amenities;
    if (a.wheelchair) amenityTags.push({ label: 'Wheelchair accessible', icon: <Accessibility size={12} /> });
    if (a.internetAccess) amenityTags.push({ label: 'Wi-Fi', icon: <Wifi size={12} /> });
    if (a.airConditioning) amenityTags.push({ label: 'Air conditioning', icon: <Snowflake size={12} /> });
    if (a.outdoorSeating) amenityTags.push({ label: 'Outdoor seating', icon: <Sun size={12} /> });
    if (a.delivery) amenityTags.push({ label: 'Delivery', icon: <Truck size={12} /> });
    if (a.takeaway) amenityTags.push({ label: 'Takeaway', icon: <ShoppingBag size={12} /> });
    if (a.parking) amenityTags.push({ label: `Parking: ${a.parking}`, icon: <Car size={12} /> });
    if (a.driveThrough) amenityTags.push({ label: 'Drive-through', icon: <Car size={12} /> });
  }

  // --- Rating handler (auth required) ---
  const { isAuthenticated } = useAuthStore();
  const handleRatePlace = async (stars: number) => {
    if (!selectedPlace.id || isRating) return;
    requireAuth(async () => {
      setUserRating(stars);
      setIsRating(true);
      try {
        const res = await ratePlace(selectedPlace.id, stars);
        setUserRating(res.user_rating);
        setRatingSubmitted(true);
        selectPlace(selectedPlace.id);
        setTimeout(() => setRatingSubmitted(false), 3000);
      } catch (err) {
        console.error('Failed to rate:', err);
      } finally {
        setIsRating(false);
      }
    });
  };

  // --- Interactive star rating ---
  const renderStarRating = () => {
    const displayRating = hoverRating || userRating || 0;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Star size={16} className="text-yellow-500" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rate this place</h3>
          </div>
          {selectedPlace.rating && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-yellow-600 dark:text-yellow-400">{selectedPlace.rating.toFixed(1)}</span>
              {selectedPlace.reviewCount != null && (
                <span className="text-xs text-gray-400 dark:text-gray-500">({selectedPlace.reviewCount})</span>
              )}
            </div>
          )}
        </div>
        {isAuthenticated ? (
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => handleRatePlace(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                disabled={isRating}
                className="p-1 transition-transform hover:scale-110 disabled:opacity-50"
                title={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                <Star
                  size={28}
                  className={`transition-colors ${star <= displayRating
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300 dark:text-gray-600'
                    }`}
                />
              </button>
            ))}
            {isRating && <Loader2 size={18} className="ml-2 animate-spin text-yellow-500" />}
            {ratingSubmitted && <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">Thanks!</span>}
            {userRating > 0 && !ratingSubmitted && <span className="ml-2 text-xs text-gray-400">Your rating: {userRating}</span>}
          </div>
        ) : (
          <button
            onClick={() => requireAuth(() => { })}
            className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
          >
            <Star size={20} className="text-gray-300" />
            <span>Sign in to rate this place</span>
          </button>
        )}
      </div>
    );
  };

  // --- Inline edit input row ---
  const renderInlineEdit = (_field: EditableField, placeholder: string) => (
    <div className="mt-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={editValue}
          onChange={(e) => { setEditValue(e.target.value); setEditError(''); }}
          placeholder={placeholder}
          className={`flex-1 text-sm px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 border ${editError ? 'border-red-400/60 dark:border-red-500/40' : 'border-gray-200/80 dark:border-gray-600/40'} transition-all`}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
        />
        <button onClick={saveEdit} disabled={isSaving || !editValue.trim()} className="p-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-all shadow-sm shadow-primary-500/20">
          {isSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        </button>
        <button onClick={cancelEdit} className="p-2.5 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all">
          <X size={15} />
        </button>
      </div>
      {editError && <p className="text-xs text-red-500 dark:text-red-400 pl-1 font-medium">{editError}</p>}
    </div>
  );

  // --- Phone number inline edit with auto-format ---
  const formatPhoneValue = (raw: string): string => {
    const hasPlus = raw.startsWith('+');
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return hasPlus ? '+' : '';

    // International format: +233 XX XXX XXXX
    if (hasPlus || digits.startsWith('233')) {
      const parts: string[] = [];
      if (digits.length > 0) parts.push('+' + digits.slice(0, 3));
      if (digits.length > 3) parts.push(digits.slice(3, 5));
      if (digits.length > 5) parts.push(digits.slice(5, 8));
      if (digits.length > 8) parts.push(digits.slice(8, 12));
      return parts.join(' ');
    }

    // Local format: 0XX XXX XXXX
    const parts: string[] = [];
    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length > 3) parts.push(digits.slice(3, 6));
    if (digits.length > 6) parts.push(digits.slice(6, 10));
    return parts.join(' ');
  };

  const renderPhoneEdit = (placeholder: string) => (
    <div className="mt-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <input
          type="tel"
          inputMode="tel"
          value={editValue}
          onChange={(e) => {
            const formatted = formatPhoneValue(e.target.value);
            setEditValue(formatted);
            setEditError('');
          }}
          placeholder={placeholder}
          maxLength={16}
          className={`flex-1 text-sm px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 border font-mono tracking-wide ${editError ? 'border-red-400/60 dark:border-red-500/40' : 'border-gray-200/80 dark:border-gray-600/40'} transition-all`}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
        />
        <button onClick={saveEdit} disabled={isSaving || !editValue.trim()} className="p-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-all shadow-sm shadow-primary-500/20">
          {isSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        </button>
        <button onClick={cancelEdit} className="p-2.5 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all">
          <X size={15} />
        </button>
      </div>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 pl-1">Format: +233 XX XXX XXXX or 0XX XXX XXXX</p>
      {editError && <p className="text-xs text-red-500 dark:text-red-400 pl-1 font-medium">{editError}</p>}
    </div>
  );

  // --- Ghana PostGPS inline edit with format helper ---
  const renderGPSEdit = () => (
    <div className="mt-2.5 space-y-2">
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={editValue}
          onChange={(e) => {
            setEditValue(sanitizeGhanaPostGpsInput(e.target.value));
            setEditError('');
          }}
          placeholder="e.g. AK-039-5028 or GH-GA141-2554"
          maxLength={GHANA_POST_GPS_MAX_LENGTH}
          className={`flex-1 text-sm px-3.5 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 border font-mono tracking-wider ${editError ? 'border-red-400/60 dark:border-red-500/40' : 'border-gray-200/80 dark:border-gray-600/40'} transition-all`}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
        />
        <button onClick={saveEdit} disabled={isSaving || !editValue.trim()} className="p-2.5 rounded-xl bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-all shadow-sm shadow-primary-500/20">
          {isSaving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        </button>
        <button onClick={cancelEdit} className="p-2.5 rounded-xl text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all">
          <X size={15} />
        </button>
      </div>
      <p className="text-[10px] text-gray-400 dark:text-gray-500 pl-1">Format: XX-000-0000 or GH-XX000-0000 (e.g. AK-039-5028 or GH-GA141-2554)</p>
      {editError && <p className="text-xs text-red-500 dark:text-red-400 pl-1 font-medium">{editError}</p>}
    </div>
  );

  const renderHoursEditor = () => (
    <div className="mt-3 space-y-3">
      <OpeningHoursEditor value={openingHoursEditValue} onChange={setOpeningHoursEditValue} />

      {editError && <p className="text-xs text-red-500 dark:text-red-400 pl-1 font-medium">{editError}</p>}

      <div className="flex items-center gap-2">
        <button onClick={saveEdit} disabled={isSaving} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 disabled:opacity-40 transition-all shadow-sm shadow-primary-500/20">
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
        <button onClick={cancelEdit} className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all">
          Cancel
        </button>
      </div>
    </div>
  );

  // Convenience booleans for section logic
  const hasContact = !!(selectedPlace.phone || selectedPlace.whatsapp || selectedPlace.email);
  const hasOnline = !!(selectedPlace.website || selectedPlace.facebook || selectedPlace.instagram);
  const hasHours = !!(
    selectedPlace.openingHoursStatus
    || (selectedPlace.hours
      && (selectedPlace.hours.isOpen !== null && selectedPlace.hours.isOpen !== undefined
        || selectedPlace.hours.open
        || selectedPlace.hours.close))
  );

  const hoursStatus = selectedPlace.openingHoursStatus;
  const previewStatus = contributionPreview
    ? CONTRIBUTION_PREVIEW_STATUS[contributionPreview.status]
    : null;
  const PreviewStatusIcon = previewStatus?.icon;
  const buildingUnits =
    selectedPlace.buildingUnits
    ?? (contributionPreview?.building_units
      ? normalizeBuildingUnits(contributionPreview.building_units)
      : []);
  const showBuildingUnits =
    selectedPlace.placeType === 'building' && buildingUnits.length > 0;

  return (
    <div className="space-y-4">
      {contributionPreview && previewStatus && PreviewStatusIcon && (
        <div className={`rounded-2xl border p-3.5 space-y-2 ${previewStatus.panel}`}>
          <div className="flex items-start gap-2.5">
            <Eye size={18} className="text-primary-600 dark:text-primary-400 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
                Map preview
              </p>
              <p className="text-xs text-gray-600 dark:text-neutral-400 mt-0.5">
                How this place will appear on the map after approval.
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${previewStatus.badge}`}>
              <PreviewStatusIcon size={11} />
              {previewStatus.label}
            </span>
          </div>
          {contributionPreview.reviewer_feedback && (
            <p className="text-xs text-gray-700 dark:text-neutral-300 pl-7">
              <strong>Reviewer:</strong> {contributionPreview.reviewer_feedback}
            </p>
          )}
          {contributionPreview.photos && contributionPreview.photos.length > 0 && (
            <div className="pl-7 pt-1">
              <div className="grid grid-cols-3 gap-2">
                {contributionPreview.photos.map((url, index) => (
                  <a
                    key={`${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-xl overflow-hidden border border-gray-200/80 dark:border-neutral-700"
                  >
                    <img src={url} alt={`Submission photo ${index + 1}`} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Close Button */}
      <div className="flex justify-end -mb-2 -mt-1">
        <button onClick={handleClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" title="Close">
          <X size={20} className="text-gray-400" />
        </button>
      </div>

      {/* Header */}
      <div className="relative">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-200 dark:shadow-primary-900/30">
            <DynamicIcon name={getCategoryIconName(selectedPlace.category)} size={28} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{selectedPlace.name}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                <Tag size={12} className="mr-1" />{getCategoryLabel(selectedPlace.category)}
              </span>
              {selectedPlace.brand && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">{selectedPlace.brand}</span>
              )}
              {selectedPlace.cuisine && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <Utensils size={10} className="mr-1" />{selectedPlace.cuisine}
                </span>
              )}
              {showFloodExposure && floodRiskLevel && (
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${floodRiskBadgeClass(floodRiskLevel)}`}
                  title={selectedPlace.floodProne ? 'Often flood-prone during the rainy season' : 'Seasonal flood risk based on long-term rainfall patterns'}
                >
                  <CloudRain size={11} className="flex-shrink-0" strokeWidth={2.25} />
                  {floodRiskLabel(floodRiskLevel)}
                </span>
              )}
            </div>
            {selectedPlace.rating && showBusinessDetails && (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/30 px-2 py-1 rounded-lg">
                  <Star size={14} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">{selectedPlace.rating.toFixed(1)}</span>
                </div>
                {selectedPlace.reviewCount && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">{selectedPlace.reviewCount.toLocaleString()} reviews</span>
                )}
              </div>
            )}
            {(displayDistance || walkTime) && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {displayDistance && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md">
                    <Ruler size={10} />{displayDistance}
                  </span>
                )}
                {walkTime && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-md">
                    <Footprints size={10} />{walkTime}
                  </span>
                )}
                {bikeTime && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 rounded-md">
                    <Bike size={10} />{bikeTime}
                  </span>
                )}
                {driveTime && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-md">
                    <Car size={10} />{driveTime}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showBuildingUnits && (
        <BuildingUnitsDetailSection units={buildingUnits} />
      )}

      {hasTransitStopDetails && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bus size={16} className="text-emerald-600 dark:text-emerald-400" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Stop details</h3>
          </div>
          <div className="space-y-3">
            {transitDescription && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">About this stop</p>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                  {transitDescription}
                  {transitDescriptionSourceUrl && (
                    <>
                      {' '}
                      <a
                        href={transitDescriptionSourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                      >
                        More
                      </a>
                    </>
                  )}
                </p>
              </div>
            )}
            {selectedPlace.stopRef?.trim() && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">Stop reference</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{selectedPlace.stopRef.trim()}</p>
              </div>
            )}
            {transitRoutes.length > 0 && (
              <TransitStopRouteList routes={transitRoutes} />
            )}
            {transitOperators.length > 0 && (
              <TransitStopOperatorList operators={transitOperators} />
            )}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Facilities</p>
              <div className="flex flex-wrap gap-2">
                {TRANSIT_STOP_FACILITIES.map(({ key, label, Icon }) => {
                  const available = transitFacilityValues?.[key];
                  return (
                    <span
                      key={key}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium ${
                        available
                          ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      <Icon size={14} className={available ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-300 dark:text-gray-600'} />
                      {label}
                      {available ? (
                        <CheckCircle2 size={12} className="text-emerald-600 dark:text-emerald-400" />
                      ) : null}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About — POI/building; transit stops show description in Stop details */}
      {!isTransitStop && selectedPlace.description && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Info size={16} className="text-gray-500" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">About this place</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-line">
            {selectedPlace.description}
            {selectedPlace.descriptionSourceUrl?.trim() && (
              <>
                {' '}
                <a
                  href={selectedPlace.descriptionSourceUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                >
                  More
                </a>
              </>
            )}
          </p>
        </div>
      )}

      {/* Admin place / locality codes or building postcode */}
      {((isAdminPlace && (coordalCode || ghanaPostGpsCode || showGssCode)) || (showBusinessDetails && (formattedPostcode || selectedPlace.ghanaPostGPS))) && (
        <div className="bg-gradient-to-r from-primary-50 to-green-50 dark:from-primary-900/30 dark:to-green-900/30 rounded-2xl p-4 border border-primary-100 dark:border-primary-800 space-y-3">
          {isAdminPlace && coordalCode && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {coordalAdminCodeLabel(selectedPlace.category)}
                  </p>
                  <p className="text-lg font-bold text-primary-700 dark:text-primary-400 font-mono">{coordalCode}</p>
                </div>
              </div>
              <button onClick={handleCopyPostcode} className="p-2.5 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" title={copiedPostcode ? 'Copied!' : 'Copy code'}>
                {copiedPostcode ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-gray-500" />}
              </button>
            </div>
          )}
          {isAdminPlace && ghanaPostGpsCode && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm">
                  <MapPin size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {ghanaPostGpsAdminCodeLabel(selectedPlace.category)}
                  </p>
                  <p className="text-lg font-bold text-green-700 dark:text-green-400 font-mono">{ghanaPostGpsCode}</p>
                </div>
              </div>
              <button onClick={handleCopyGPS} className="p-2.5 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" title={copiedGPS ? 'Copied!' : 'Copy Ghana PostGPS code'}>
                {copiedGPS ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-gray-500" />}
              </button>
            </div>
          )}
          {isAdminPlace && showGssCode && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm">
                <Tag size={20} className="text-indigo-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {gssOfficialCodeLabel(selectedPlace.category)}
                </p>
                <p className="text-lg font-bold text-indigo-700 dark:text-indigo-400 font-mono">{gssCode}</p>
              </div>
            </div>
          )}
          {!isAdminPlace && formattedPostcode && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm">
                  <Building2 size={20} className="text-primary-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Postcode</p>
                  <p className="text-lg font-bold text-primary-700 dark:text-primary-400 font-mono">{formattedPostcode}</p>
                </div>
              </div>
              <button onClick={handleCopyPostcode} className="p-2.5 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" title={copiedPostcode ? 'Copied!' : 'Copy postcode'}>
                {copiedPostcode ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-gray-500" />}
              </button>
            </div>
          )}
          {showBusinessDetails && (
            selectedPlace.ghanaPostGPS ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center shadow-sm">
                  <MapPin size={20} className="text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ghana PostGPS</p>
                  <p className="text-lg font-bold text-green-700 dark:text-green-400 font-mono">{selectedPlace.ghanaPostGPS}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => startEdit('ghana_post_gps')} className="p-2 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" title="Edit Ghana PostGPS">
                  <PenLine size={14} className="text-gray-400" />
                </button>
                <button onClick={handleCopyGPS} className="p-2.5 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm" title={copiedGPS ? 'Copied!' : 'Copy'}>
                  {copiedGPS ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-gray-500" />}
                </button>
              </div>
            </div>
          ) : editingField === 'ghana_post_gps' ? (
              <div className="mt-1">{renderGPSEdit()}</div>
            ) : (
              <button onClick={() => startEdit('ghana_post_gps')} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/60 dark:hover:bg-gray-800/40 transition-colors text-left">
                <div className="w-10 h-10 bg-white/70 dark:bg-gray-800/70 rounded-xl flex items-center justify-center shadow-sm">
                  <MapPin size={20} className="text-gray-400" />
                </div>
                <p className="flex-1 text-sm text-gray-500 dark:text-gray-400">Add Ghana PostGPS</p>
                <PenLine size={14} className="text-gray-400 flex-shrink-0" />
              </button>
            )
          )}
        </div>
      )}

      {/* Quick Action Buttons */}
      <div className="flex gap-2">
        <button onClick={handleGetDirections} className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3.5 px-4 rounded-2xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all shadow-lg shadow-primary-200 dark:shadow-primary-900/30">
          <Navigation size={20} />Get Directions
        </button>
        <button
          onClick={handleSavePlace}
          className={`flex items-center justify-center w-14 h-14 rounded-2xl transition-all shadow-md ${isSaved ? 'bg-primary-100 dark:bg-primary-900/50 hover:bg-primary-200' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
          title={isSaved ? 'Remove from saved' : 'Save place'}
        >
          {isSaved ? <BookmarkCheck size={22} className="text-primary-600" /> : <Bookmark size={22} className="text-gray-500" />}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center justify-center w-14 h-14 bg-white dark:bg-gray-800 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-md relative"
          title={copiedShare ? 'Link copied!' : 'Share'}
        >
          {copiedShare ? <Check size={22} className="text-green-500" /> : <Share2 size={22} className="text-gray-500" />}
        </button>
      </div>

      {/* Add Missing Details button — DB-backed places only */}
      {isDbPlace && onSuggestEdit && (
        <button
          onClick={() => requireAuth(onSuggestEdit)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all text-sm font-medium"
        >
          <PenLine size={15} />
          Add Missing Details
        </button>
      )}

      {/* ─── DETAILS SECTIONS ─── */}
      <div className="space-y-3">

        {showFloodExposure && floodRiskLevel && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3">
              <CloudRain size={16} className="text-sky-600 dark:text-sky-400" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Seasonal flood exposure</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-xl ${floodRiskBadgeClass(floodRiskLevel)}`}>
                  <CloudRain size={14} strokeWidth={2.25} />
                  {floodRiskLabel(floodRiskLevel)}
                </span>
                {selectedPlace.floodProne && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                    Flood-prone segment
                  </span>
                )}
              </div>
              {selectedPlace.avgRainfallMm != null && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <span className="font-medium text-gray-700 dark:text-gray-200">Avg. seasonal rainfall:</span>{' '}
                  {Math.round(selectedPlace.avgRainfallMm)} mm
                </p>
              )}
              {selectedPlace.seasonalAccessNotes?.trim() && (
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {selectedPlace.seasonalAccessNotes.trim()}
                </p>
              )}
              <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
                {FLOOD_EXPOSURE_DISCLAIMER}
              </p>
            </div>
          </div>
        )}

        {/* Address Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Address</p>
              <p className="text-gray-900 dark:text-white font-medium leading-relaxed">
                {nativeAddress}
              </p>
            </div>
            <button onClick={handleCopyAddress} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0" title={copiedAddress ? 'Copied!' : 'Copy address'}>
              {copiedAddress ? <Check size={18} className="text-green-600" /> : <Copy size={18} className="text-gray-400" />}
            </button>
          </div>
        </div>

        {showBusinessDetails && (
        <>
        {/* ═══ Opening Hours Section ═══ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-orange-500" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Opening Hours</h3>
            </div>
            {isDbPlace && hasHours && editingField !== 'opening_hours' && (
              <button onClick={() => startEdit('opening_hours')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Edit hours">
                <PenLine size={12} className="text-gray-400" />
              </button>
            )}
          </div>
          {editingField === 'opening_hours' ? (
            renderHoursEditor()
          ) : hasHours ? (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {hoursStatus ? (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${placeHoursBadgeClass(hoursStatus)}`}>
                  {formatHoursForSubtitle(hoursStatus)}
                </span>
              ) : (
                <>
                  {selectedPlace.hours?.isOpen === true && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      Open now
                    </span>
                  )}
                  {selectedPlace.hours?.isOpen === false && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                      Closed
                    </span>
                  )}
                </>
              )}
            </div>
          ) : isDbPlace ? (
            <button onClick={() => startEdit('opening_hours')} className="flex items-center gap-2 mt-1 text-sm text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors group">
              <span>No hours available</span>
              <PenLine size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">No hours available</p>
          )}
        </div>

        {/* ═══ Rating Section ═══ */}
        {!contributionPreview && renderStarRating()}

        {/* ═══ Contact Section ═══ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Phone size={16} className="text-green-500" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Contact</h3>
          </div>
          {hasContact ? (
            <div className="space-y-2.5">
              {/* Phone */}
              {selectedPlace.phone && (
                editingField === 'phone' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <div className="flex items-center justify-between group">
                    <button onClick={() => handleCall(selectedPlace.phone!)} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                      <Phone size={15} className="text-green-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{selectedPlace.phone}</span>
                    </button>
                    <button onClick={() => startEdit('phone')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all" title="Edit phone">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* WhatsApp */}
              {selectedPlace.whatsapp && (
                editingField === 'whatsapp' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <div className="flex items-center justify-between group">
                    <a href={`https://wa.me/${selectedPlace.whatsapp.replace(/[^0-9+]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
                      <MessageCircle size={15} className="text-green-500" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{selectedPlace.whatsapp}</span>
                      <span className="text-[10px] text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-1.5 py-0.5 rounded-full font-medium">WhatsApp</span>
                    </a>
                    <button onClick={() => startEdit('whatsapp')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all" title="Edit WhatsApp">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* Email */}
              {selectedPlace.email && (
                editingField === 'email' ? renderInlineEdit('email', 'name@example.com') : (
                  <div className="flex items-center justify-between group">
                    <a href={`mailto:${selectedPlace.email}`} className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
                      <Mail size={15} className="text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{selectedPlace.email}</span>
                    </a>
                    <button onClick={() => startEdit('email')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" title="Edit email">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* Missing fields — subtle add affordances (DB places only) */}
              {isDbPlace && !selectedPlace.phone && (
                editingField === 'phone' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <button onClick={() => startEdit('phone')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Phone size={13} className="text-gray-300 group-hover:text-primary-400" />
                    <span>Add phone</span>
                  </button>
                )
              )}
              {isDbPlace && !selectedPlace.whatsapp && (
                editingField === 'whatsapp' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <button onClick={() => startEdit('whatsapp')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <MessageCircle size={13} className="text-gray-300 group-hover:text-primary-400" />
                    <span>Add WhatsApp</span>
                  </button>
                )
              )}
              {isDbPlace && !selectedPlace.email && (
                editingField === 'email' ? renderInlineEdit('email', 'name@example.com') : (
                  <button onClick={() => startEdit('email')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Mail size={13} className="text-gray-300 group-hover:text-primary-400" />
                    <span>Add email</span>
                  </button>
                )
              )}
            </div>
          ) : isDbPlace ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-400 dark:text-gray-500">No contact information yet</p>
              <div className="flex flex-wrap gap-3">
                {editingField === 'phone' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <button onClick={() => startEdit('phone')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Phone size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add phone</span>
                  </button>
                )}
                {editingField === 'whatsapp' ? renderPhoneEdit('+233 XX XXX XXXX') : (
                  <button onClick={() => startEdit('whatsapp')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <MessageCircle size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add WhatsApp</span>
                  </button>
                )}
                {editingField === 'email' ? renderInlineEdit('email', 'name@example.com') : (
                  <button onClick={() => startEdit('email')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Mail size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add email</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No contact information</p>
          )}
        </div>

        {/* ═══ Online Presence Section ═══ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe size={16} className="text-purple-500" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Online Presence</h3>
          </div>
          {hasOnline ? (
            <div className="space-y-2.5">
              {/* Website */}
              {selectedPlace.website && (
                editingField === 'website' ? renderInlineEdit('website', 'https://...') : (
                  <div className="flex items-center justify-between group">
                    <a href={selectedPlace.website.startsWith('http') ? selectedPlace.website : `https://${selectedPlace.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
                      <Globe size={15} className="text-purple-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400 truncate">{selectedPlace.website.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink size={12} className="text-gray-400 flex-shrink-0" />
                    </a>
                    <button onClick={() => startEdit('website')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" title="Edit website">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* Facebook */}
              {selectedPlace.facebook && (
                editingField === 'facebook' ? renderInlineEdit('facebook', 'Page name or URL') : (
                  <div className="flex items-center justify-between group">
                    <a href={selectedPlace.facebook.startsWith('http') ? selectedPlace.facebook : `https://facebook.com/${selectedPlace.facebook}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
                      <Facebook size={15} className="text-blue-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{selectedPlace.facebook}</span>
                    </a>
                    <button onClick={() => startEdit('facebook')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" title="Edit Facebook">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* Instagram */}
              {selectedPlace.instagram && (
                editingField === 'instagram' ? renderInlineEdit('instagram', '@username') : (
                  <div className="flex items-center justify-between group">
                    <a href={selectedPlace.instagram.startsWith('http') ? selectedPlace.instagram : `https://instagram.com/${selectedPlace.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity min-w-0">
                      <Instagram size={15} className="text-pink-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">@{selectedPlace.instagram.replace(/^@/, '')}</span>
                    </a>
                    <button onClick={() => startEdit('instagram')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" title="Edit Instagram">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                  </div>
                )
              )}
              {/* Missing online fields (DB places only) */}
              {isDbPlace && !selectedPlace.website && (
                editingField === 'website' ? renderInlineEdit('website', 'https://...') : (
                  <button onClick={() => startEdit('website')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Globe size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add website</span>
                  </button>
                )
              )}
              {isDbPlace && !selectedPlace.facebook && (
                editingField === 'facebook' ? renderInlineEdit('facebook', 'Page name or URL') : (
                  <button onClick={() => startEdit('facebook')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Facebook size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add Facebook</span>
                  </button>
                )
              )}
              {isDbPlace && !selectedPlace.instagram && (
                editingField === 'instagram' ? renderInlineEdit('instagram', '@username') : (
                  <button onClick={() => startEdit('instagram')} className="flex items-center gap-2 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Instagram size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add Instagram</span>
                  </button>
                )
              )}
            </div>
          ) : isDbPlace ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-400 dark:text-gray-500">No online presence yet</p>
              <div className="flex flex-wrap gap-3">
                {editingField === 'website' ? renderInlineEdit('website', 'https://...') : (
                  <button onClick={() => startEdit('website')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Globe size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add website</span>
                  </button>
                )}
                {editingField === 'facebook' ? renderInlineEdit('facebook', 'Page name or URL') : (
                  <button onClick={() => startEdit('facebook')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Facebook size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add Facebook</span>
                  </button>
                )}
                {editingField === 'instagram' ? renderInlineEdit('instagram', '@username') : (
                  <button onClick={() => startEdit('instagram')} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary-500 transition-colors group">
                    <Instagram size={13} className="text-gray-300 group-hover:text-primary-400" /><span>Add Instagram</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No online presence</p>
          )}
        </div>
        </>
        )}

        {/* ═══ Postal References Section (POI only; admin codes shown above) ═══ */}
        {showBusinessDetails && (selectedPlace.ghanaPostGPS || !formattedPostcode) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} className="text-green-500" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Other References</h3>
            </div>
            {selectedPlace.ghanaPostGPS ? (
              editingField === 'ghana_post_gps' ? renderGPSEdit() : (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">GhanaPost GPS</span>
                    <span className="text-sm font-mono font-semibold text-gray-800 dark:text-gray-200">{selectedPlace.ghanaPostGPS}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit('ghana_post_gps')} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-all" title="Edit">
                      <PenLine size={11} className="text-gray-400" />
                    </button>
                    <button onClick={handleCopyGPS} className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title={copiedGPS ? 'Copied!' : 'Copy'}>
                      {copiedGPS ? <Check size={13} className="text-green-600" /> : <Copy size={13} className="text-gray-400" />}
                    </button>
                  </div>
                </div>
              )
            ) : (
              editingField === 'ghana_post_gps' ? renderGPSEdit() : (
                <button onClick={() => startEdit('ghana_post_gps')} className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 hover:text-primary-500 dark:hover:text-primary-400 transition-colors group">
                  <span>GhanaPost GPS not available</span>
                  <PenLine size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              )
            )}
          </div>
        )}

        {/* Amenities */}
        {showBusinessDetails && amenityTags.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Star size={16} className="text-amber-500" />
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Amenities</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {amenityTags.map((tag) => (
                <span key={tag.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {tag.icon}{tag.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Copyright */}
      <div className="pt-2 pb-4 text-center">
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          © {new Date().getFullYear()} <a href={MARKETING_URL} target="_blank" rel="noopener noreferrer" className="hover:text-primary-500 transition-colors">Coordal Technologies Ltd</a>. Reg No. CS110510526. All rights reserved.
        </p>
      </div>
    </div>
  );
}
